/**
 * DeliveryCheckScreen — Conferência de Entrega (Fase 6 · Bloco 5).
 *
 * Fiel ao protótipo: veredito (aderência por critérios) + KPIs + matriz de
 * rastreabilidade expansível (requisito → critérios PEDIDO×ENTREGUE, arquivos,
 * testes, tela, divergência) + conferência do design + barra de aceite → Gate
 * Final. Dados reais do QA (via AI Core/Claude) mapeando requisitos × artefatos.
 * Lê ?project=.
 */
import { C } from '@/theme/palette';
import { useMemo, useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useProjects } from '@/api/projects';
import { useDeliveryCheck, useGenerateDeliveryCheck, useLatestExecution } from '@/api/deliveryCheck';
import { useDeliveryAudit, useGenerateDeliveryAudit, useDeliveryAuditHistory } from '@/api/deliveryAudit';
import { useRemediateCode, useRemediate } from '@/api/executions';
import { remediationPanelState } from './remediationPanelState';
import { useIniciarAguardandoIndexacao } from '@/api/aguardarIndexacao';
import { AvisoIndexacao } from '@/components/AvisoIndexacao';

/** Os caminhos de REMEDIAÇÃO aparecem na Conferência de Entrega?
 *
 *  Desligados em 2026-09-22 por decisão do dono, os três: "Remediar — completar /
 *  consertar a entrega", "Corrigir lacunas e reprocessar" (modo análise) e "Corrigir
 *  estritamente pelo .md" (auditoria de código). Com a construção em ordem por camadas a
 *  entrega sai completa de uma vez, e o caminho de conserto passou a ser REGENERAR em vez
 *  de remendar por cima — cada remendo criava uma entrega que acumulava sobre a anterior,
 *  e era isso que embaralhava a medição de cobertura.
 *
 *  As funções continuam no código e na API: é a EXIBIÇÃO que saiu. `true` devolve tudo. */
const MOSTRAR_REMEDIACAO = false;

/** A "Auditoria de Entrega — revisão profunda de código" aparece na Conferência?
 *  Ocultada em 2026-10-03 por decisão do dono. A função continua no código e na API: é a
 *  EXIBIÇÃO que saiu (bloco, trilha de validação e botões). `true` devolve tudo. */
const MOSTRAR_AUDITORIA_DE_ENTREGA = false;
import { useRemediateAnalysis } from '@/api/analysis';
import { AiProgress } from '@/components/ui/AiProgress';
import { AiTruncationNotice } from '@/components/ui';
import { ProvaDeDadosSection } from '@/components/ProvaDeDadosSection';
import { ContratoFrontApiSection } from '@/components/ContratoFrontApiSection';
import { ProblemasDaEntregaSection } from '@/components/ProblemasDaEntregaSection';
import { RuntimeSection } from '@/components/RuntimeSection';
import { RoteiroSection } from '@/components/RoteiroSection';
import { BancadaDoAgente } from '@/components/BancadaDoAgente';
import { EvidenciasSection } from '@/components/EvidenciasSection';
import { QuemFezDaExecucao } from '@/components/QuemFezSection';
import { OrigemDaEntrega, estadoDaPagina } from '@/components/OrigemDaEntrega';
import { SeloDeGarantia, AJUDA_FINALIZACAO_NO_CLIENTE } from '@/components/SeloDeGarantia';
import { ExportacaoContext, useExpandirTudo } from '@/components/ExportacaoContext';
import { ExportarConferenciaMenu } from '@/components/ExportarConferenciaMenu';
import { useRelatorioDeGarantia } from '@/api/relatorioDeGarantia';
import { downloadFile, slugify } from './analysisExport';
import { conferenciaParaMarkdown, nomeDoArquivo } from './deliveryCheckExport';
import { montarHtmlDaConferencia, carregarFonteInter } from './deliveryCheckHtmlExport';

// Paleta: segue o tema global (aliases bg/surface/border/text/dim/muted… em theme/palette.js).

const ST = {
  atendido:     { label: 'ATENDIDO',     color: C.cyber },
  parcial:      { label: 'PARCIAL',      color: C.digital },
  nao_atendido: { label: 'NÃO ATENDIDO', color: C.hot },
};

// Auditoria de Entrega — estado REAL (por leitura de código) + veredito vs Conferência.
const AUD_ST = {
  implementado_real: { label: 'IMPLEMENTADO', color: C.cyber },
  casca:             { label: 'CASCA',        color: C.hot },
  parcial:           { label: 'PARCIAL',      color: C.digital },
  ausente:           { label: 'AUSENTE',      color: C.hot },
};
const AUD_VERDICT = {
  superestimou: { label: 'Conferência superestimou', color: C.hot },
  subestimou:   { label: 'Conferência subestimou',   color: C.quantum },
  ok:           { label: '',                         color: C.muted },
};

function KpiCard({ label, value, sub, color }) {
  return (
    <div style={{ border: `1px solid ${C.border}`, background: C.surface, borderRadius: 12, padding: 14 }}>
      <div style={{ fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.muted }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color, marginTop: 4, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: C.dim, marginTop: 6 }}>{sub}</div>}
    </div>
  );
}

// Texto ao lado do selo. No modo de entrega `client` a plataforma não executou nada: a
// frase não pode dizer "provado executando o código".
// Quem diz o modo é a API (`delivery_mode` da entrega): com pendência o estado vem
// HUMAN_REVIEW_REQUIRED. Resposta sem o campo → o estado da garantia.
export function textoDoVeredito(assurance) {
  const requisitos = assurance?.requisitos ?? [];
  const noCliente = assurance?.delivery_mode === 'client'
    || [assurance?.estado, ...(assurance?.repos ?? []).map((r) => r.estado)]
      .includes('READY_FOR_CLIENT_FINALIZATION');
  if (noCliente) {
    return `A plataforma não executou o código desta entrega. ${AJUDA_FINALIZACAO_NO_CLIENTE} `
      + 'O percentual abaixo é a leitura do QA pelos nomes dos arquivos.';
  }
  return `${requisitos.filter((r) => r.estado === 'provado').length} de ${requisitos.length} `
    + 'requisito(s) provado(s) executando o código (implementação · testes · runtime). É este '
    + 'veredito que vale para o aceite — o percentual abaixo é a leitura do QA pelos nomes dos arquivos.';
}

export function TraceItem({ r, onViewFile, analise = false }) {
  const [aberto, setOpen] = useState(false);
  // Exportação: abre a linha sem mexer no estado do usuário.
  const expandirTudo = useExpandirTudo();
  const open = aberto || expandirTudo;
  const s = ST[r.status] || ST.nao_atendido;
  const crit = Array.isArray(r.criteria) ? r.criteria : [];
  const okCrit = crit.filter((c) => c.ok).length;
  const files = Array.isArray(r.files) ? r.files : [];
  const tests = Array.isArray(r.tests) ? r.tests : [];
  const isOk = r.status === 'atendido';

  const sub = [
    crit.length ? `${okCrit}/${crit.length} critérios` : null,
    files.length ? `${files.length} arquivo${files.length !== 1 ? 's' : ''}` : null,
    tests.length ? `${tests.length} teste${tests.length !== 1 ? 's' : ''} executado${tests.length !== 1 ? 's' : ''}` : null,
    r.screen ? `tela: ${r.screen}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <div style={{ borderTop: `1px solid ${C.border}`, borderLeft: `3px solid ${s.color}` }}>
      <div onClick={() => setOpen((o) => !o)}
        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px' }}>
        <span style={{ width: 20, height: 20, borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center',
          fontSize: 11, fontWeight: 800, color: isOk ? C.black : s.color,
          background: isOk ? C.cyber : 'transparent', border: `1.5px solid ${s.color}` }}>
          {isOk ? '✓' : '!'}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, color: C.text, fontWeight: 600 }}>{r.requirement}</div>
          {sub && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{sub}</div>}
        </div>
        <span style={{ fontFamily: 'monospace', fontSize: 10, padding: '3px 9px', borderRadius: 6,
          color: s.color, background: `${s.color}1F`, whiteSpace: 'nowrap' }}>{s.label}</span>
        <span style={{ color: C.muted, transition: 'transform .15s', transform: open ? 'rotate(90deg)' : 'none' }}>›</span>
      </div>

      {open && (
        <div style={{ padding: '2px 16px 16px 46px', background: C.black }}>
          {/* Critérios: pedido × entregue */}
          {crit.length > 0 && (
            <>
              <div style={{ fontSize: 9.5, letterSpacing: '0.08em', color: C.muted, margin: '12px 0 6px' }}>
                CRITÉRIOS DE ACEITE · PEDIDO vs. ENTREGUE
              </div>
              {crit.map((c, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', margin: '5px 0' }}>
                  <span style={{ color: c.ok ? C.cyber : C.digital, flexShrink: 0 }}>{c.ok ? '✓' : '✗'}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12.5, color: C.text }}>{c.text}</div>
                    {c.evidence && (
                      <div style={{ fontSize: 11, color: c.ok ? C.muted : C.digital, marginTop: 1 }}>
                        Evidência: {c.evidence}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </>
          )}
          {/* Fallback: se não há critérios, mostra o "entregue" textual */}
          {crit.length === 0 && r.delivered && (
            <p style={{ fontSize: 12.5, color: C.dim, margin: '12px 0 0' }}>{r.delivered}</p>
          )}

          {/* Evidências: arquivos, testes, tela */}
          {/* Testes só com relatório de execução (P9). Sem ele, a Conferência diz que não
              conferiu — nunca mostra teste que ninguém rodou. */}
          {!analise && tests.length === 0 && (
            <div data-testid="testes-nao-conferidos" style={{ fontSize: 11, color: C.dim, margin: '12px 0 0' }}>
              🧪 Testes: não conferido — sem relatório de execução
            </div>
          )}
          {(files.length > 0 || tests.length > 0 || r.screen || (r.evidence || []).length > 0) && (
            <>
              <div style={{ fontSize: 9.5, letterSpacing: '0.08em', color: C.muted, margin: '14px 0 6px' }}>
                EVIDÊNCIAS DE IMPLEMENTAÇÃO
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {files.map((f, i) => (
                  <button key={`f${i}`} onClick={() => onViewFile(f)} title="Inspecionar arquivo gerado" data-export="estatico"
                    style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer',
                      fontFamily: 'monospace', fontSize: 10.5, padding: '3px 8px', borderRadius: 6,
                      color: C.cyber, background: `${(f.type === 'A' ? C.cyber : C.quantum)}1F`,
                      border: `1px solid ${(f.type === 'A' ? C.cyber : C.quantum)}44` }}>
                    <span>📄</span>{fileBase(f.name)}{f.changes ? ` ${f.changes}` : ''} <span style={{ opacity: 0.6 }}>👁</span>
                  </button>
                ))}
                {tests.map((t, i) => (
                  <span key={`t${i}`}
                    style={{ display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'monospace',
                    fontSize: 10.5, padding: '3px 8px', borderRadius: 6, background: C.surface2,
                    color: t.ok ? C.cyber : C.hot, border: `1px solid ${C.border}` }}>
                    🧪 {t.name} · {t.ok ? 'passou' : 'falhou'}
                  </span>
                ))}
                {r.screen && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'monospace',
                    fontSize: 10.5, padding: '3px 8px', borderRadius: 6, background: `${C.quantum}1F`,
                    color: C.quantum, border: `1px solid ${C.quantum}44` }}>🎨 {r.screen}</span>
                )}
                {/* evidências antigas (sem files estruturados) */}
                {files.length === 0 && (r.evidence || []).map((e, i) => (
                  <span key={`e${i}`} style={{ fontFamily: 'monospace', fontSize: 10.5, padding: '3px 8px',
                    borderRadius: 6, background: C.surface2, color: C.muted, border: `1px solid ${C.border}` }}>{e}</span>
                ))}
              </div>
            </>
          )}

          {/* Divergência */}
          {r.divergence && (r.divergence.text || r.divergence.action) && (
            <div style={{ marginTop: 14, padding: '10px 12px', borderRadius: 8,
              background: `${C.digital}0A`, border: `1px solid ${C.digital}44` }}>
              <div style={{ fontSize: 12, color: C.digital, fontWeight: 600 }}>
                ⚠ Divergência{r.divergence.severity ? ` (${r.divergence.severity})` : ''}: {r.divergence.text}
              </div>
              {r.divergence.action && (
                <div style={{ fontSize: 12, color: C.dim, marginTop: 6 }}>
                  <strong style={{ color: C.text }}>Ação recomendada:</strong> {r.divergence.action}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function fileBase(name = '') {
  const parts = String(name).split('/');
  return parts[parts.length - 1] || name;
}

function FileViewerModal({ file, onClose, enviada = false }) {
  if (!file) return null;
  const created = file.type === 'A';
  const accent = created ? C.cyber : C.quantum;
  return (
    <div data-export="omitir" onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 120, background: 'rgba(0,0,0,0.6)',
      backdropFilter: 'blur(3px)', display: 'grid', placeItems: 'center', padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(720px, 96vw)', maxHeight: '86vh', overflow: 'auto',
        background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontFamily: 'monospace', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 5,
            color: accent, background: `${accent}22`, border: `1px solid ${accent}55` }}>
            {created ? 'CRIADO' : 'MODIFICADO'}
          </span>
          <span style={{ fontFamily: 'monospace', fontSize: 12.5, color: C.text, flex: 1, wordBreak: 'break-all' }}>{file.name}</span>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: C.dim, cursor: 'pointer', fontSize: 18 }}>✕</button>
        </div>
        <div style={{ fontSize: 11.5, color: C.muted, marginTop: 8 }}>
          {file.changes ? `${file.changes} · ` : ''}artefato gerado pela squad na execução (sandbox).
        </div>
        <div style={{ marginTop: 14, padding: 14, borderRadius: 8, background: '#08090A', border: `1px solid ${C.border}`,
          fontFamily: 'monospace', fontSize: 11.5, color: C.dim, lineHeight: 1.6 }}>
          {enviada
            ? 'Código completo do arquivo disponível no Pull Request da execução.'
            : 'A entrega ainda não foi enviada ao GitHub: o conteúdo está na entrega pendente (tela Saída).'}
          <br />A conferência acima vincula este arquivo como evidência do requisito.
        </div>
        <div style={{ fontSize: 11, color: C.muted, marginTop: 12 }}>
          Trilha de auditoria vinculada
        </div>
      </div>
    </div>
  );
}

export function DeliveryCheckScreen() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const urlProject = searchParams.get('project');
  const { data: projectsRaw } = useProjects();
  const projects = useMemo(
    () => (Array.isArray(projectsRaw) ? projectsRaw : projectsRaw?.items ?? []),
    [projectsRaw],
  );
  const projectId = urlProject || projects?.[0]?.id || null;
  const project = useMemo(
    () => projects.find((p) => String(p.id) === String(projectId)) || null,
    [projects, projectId],
  );
  const isAnalise = (project?.deliveryScope || project?.delivery_scope) === 'analise';
  const { data, isLoading } = useDeliveryCheck(projectId);
  const generate = useGenerateDeliveryCheck(projectId);
  const { data: latestExec } = useLatestExecution(projectId);
  const executionId = latestExec?.executionId || null;
  const remediate = useRemediateAnalysis(executionId);
  const auditQuery = useDeliveryAudit(projectId);
  const genAudit = useGenerateDeliveryAudit(projectId);
  const audit = genAudit.data ?? auditQuery.data ?? null;
  const remediateCode = useRemediateCode();
  const remediateNew = useRemediate();   // "Remediar por prompt" (dirigida pelo cliente)
  // 409 "contexto não indexado": espera visível + repetição automática.
  const { iniciar: iniciarRemedCode, estado: indexRemedCode } =
    useIniciarAguardandoIndexacao(remediateCode.mutateAsync);
  const { iniciar: iniciarRemedNew, estado: indexRemedNew } =
    useIniciarAguardandoIndexacao(remediateNew.mutateAsync);
  const aguardaRemedCode = indexRemedCode.fase === 'indexando';
  const aguardaRemedNew = indexRemedNew.fase === 'indexando';
  const { data: auditHistory } = useDeliveryAuditHistory(projectId);
  const [remedStarted, setRemedStarted] = useState(false);
  const [comment, setComment] = useState('');
  const [viewFile, setViewFile] = useState(null);
  const [remedInfo, setRemedInfo] = useState(null);   // {gapsAddressed, gapsTotal} da última rodada
  const [remPrompt, setRemPrompt] = useState('');     // texto livre do cliente
  const [remSurface, setRemSurface] = useState('');   // '' auto | 'web' | 'mobile'
  const [remLaunched, setRemLaunched] = useState(null); // resposta do /remediate
  const busy = generate.isPending || remediate.isPending;
  const pagina = estadoDaPagina(data?.entrega, { analise: isAnalise });
  // Exportação (.html/.md): o modo `expandirTudo` abre todas as linhas só durante a captura.
  const conteudoRef = useRef(null);
  const [expandirTudo, setExpandirTudo] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [erroExport, setErroExport] = useState('');
  const exportacao = useMemo(() => ({ expandirTudo }), [expandirTudo]);
  const { data: quemFez } = useRelatorioDeGarantia(isAnalise ? null : data?.assurance?.executionId);

  async function exportar(formato) {
    const agora = new Date();
    const nomeProjeto = project?.name || String(projectId || '');
    const arquivo = nomeDoArquivo(slugify(nomeProjeto || 'projeto'), formato, agora);
    setErroExport('');
    if (formato === 'md') {
      downloadFile(arquivo, conferenciaParaMarkdown({
        projeto: nomeProjeto, data, analise: isAnalise, quemFez, comentario: comment, geradoEm: agora,
      }), 'text/markdown');
      return;
    }
    setExportando(true);
    try {
      const fonteDataUri = await carregarFonteInter();
      flushSync(() => setExpandirTudo(true));
      let html;
      try {
        html = montarHtmlDaConferencia({
          container: conteudoRef.current, projeto: nomeProjeto, geradoEm: agora, fonteDataUri,
        });
      } finally {
        flushSync(() => setExpandirTudo(false));
      }
      downloadFile(arquivo, html, 'text/html');
    } catch (e) {
      setErroExport(String(e?.message || e));
    } finally {
      setExportando(false);
    }
  }

  // "Remediar por prompt": dispara remediação dirigida (brownfield, acumula, PR proposta).
  async function runDirectedRemediation() {
    try {
      const res = await iniciarRemedNew({
        projectId, prompt: remPrompt.trim(), surface: remSurface,
      });
      setRemLaunched(res);
      setRemPrompt('');
      setRemSurface('');
    } catch {
      /* erros exibidos pelos estados de mutation */
    }
  }

  // Antes de concluir a entrega (análise): roda a Conferência automaticamente na
  // primeira visita, se ainda não houver matriz. Uma única vez por projeto.
  const autoRan = useRef(null);
  useEffect(() => {
    if (!isAnalise || !projectId) return;
    if (isLoading || busy) return;
    if ((data?.rows ?? []).length > 0) return;
    if (autoRan.current === projectId) return;
    autoRan.current = projectId;
    generate.mutate();
  }, [isAnalise, projectId, isLoading, busy, data, generate]);

  // Uma rodada semi-auto: corrige as lacunas (analista) e reconfere a entrega.
  async function runRemediation() {
    if (!executionId) return;
    try {
      const res = await remediate.mutateAsync();
      setRemedInfo({ gapsAddressed: res?.gapsAddressed ?? 0, gapsTotal: res?.gapsTotal ?? 0 });
      await generate.mutateAsync();
    } catch {
      /* erros exibidos pelos estados de mutation */
    }
  }

  if (!projectId) return <div style={{ padding: 40, color: C.dim }}>Nenhum projeto selecionado.</div>;

  const rows = data?.rows ?? [];
  const design = data?.design ?? [];
  const okReqs = rows.filter((r) => r.status === 'atendido').length;
  const divergences = rows.filter((r) => r.status !== 'atendido' || r.divergence).length;
  const criteriaOk = data?.criteriaOk ?? 0;
  const criteriaTotal = data?.criteriaTotal ?? 0;
  // Veredito: aderência por critérios (fallback ao veredito por status).
  const score = criteriaTotal > 0 ? (data?.coveragePct ?? 0) : (data?.verdictPct ?? 0);
  const scoreColor = score >= 90 ? C.cyber : score >= 50 ? C.digital : C.hot;
  const okScreens = design.filter((d) => d.ok).length;
  const surfaceCoverage = data?.surfaceCoverage ?? {};   // {web:{screens,pct}, mobile:{...}}
  const surfaceGap = data?.surfaceGap ?? [];             // superfícies de UI rasas
  // Cobertura de TELAS do design aprovado (ex.: 78) — número HONESTO que o cliente vê. É
  // separado da aderência por critérios/requisitos (score acima), que mede os ~28 requisitos
  // já resumidos do design e por isso ficava alto mesmo com dezenas de telas faltando.
  const screensTotal = data?.screensTotal ?? design.length;
  // Uma remediação por vez: bloqueia enquanto houver execução ativa no projeto.
  const remState = remediationPanelState({
    latestExec, launched: remLaunched, pending: remediateNew.isPending || aguardaRemedNew,
    prompt: remPrompt, surface: remSurface,
  });
  const screensOk = data?.screensOk ?? okScreens;
  const designPct = data?.designCoveragePct ?? (screensTotal ? Math.round((100 * screensOk) / screensTotal) : 0);
  const designColor = designPct >= 90 ? C.cyber : designPct >= 50 ? C.digital : C.hot;

  const goCorrections = () => navigate(`/project?project=${projectId}`);
  const goAccept = () => navigate(`/gates/final?project=${projectId}`);

  // .md de correção (mesmo formato do backend, montado no cliente a partir das linhas
  // da auditoria) — contrato determinístico p/ a remediação estrita (P2).
  function downloadAuditSpec() {
    const gaps = (audit?.rows ?? []).filter((r) => r.realStatus !== 'implementado_real');
    const md = ['# Spec de correção — Auditoria de Entrega', '',
      '> Contrato DETERMINÍSTICO. A remediação deve implementar ESTRITAMENTE os itens abaixo — nada fora desta lista.', ''];
    if (!gaps.length) md.push('_Nenhum gap: todos os requisitos estão implementados de verdade._');
    gaps.forEach((r, i) => {
      md.push(`## AUD-${String(i + 1).padStart(2, '0')} · ${r.requirement}`);
      md.push(`- **Estado atual:** ${r.realStatus}${r.gapKind ? ` (${r.gapKind})` : ''}`);
      md.push(`- **O que falta / arquivos-alvo / critério de aceite:** ${r.detail || '(a detalhar)'}`);
      if ((r.evidence ?? []).length) md.push(`- **Evidência atual:** ${r.evidence.join('; ')}`);
      md.push('');
    });
    const blob = new Blob([md.join('\n')], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'correcao-auditoria.md';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }

  // P2 — Remediação estrita: dispara um codegen que corrige SÓ os itens do .md, no repo
  // existente (brownfield). Depois o dono acompanha no Live Board e reauditar fecha o loop.
  async function runStrictRemediation() {
    if (!projectId) return;
    try {
      await iniciarRemedCode(projectId);
      setRemedStarted(true);
    } catch {
      /* erro exibido pelo estado da mutation */
    }
  }

  return (
    <ExportacaoContext.Provider value={exportacao}>
    <div ref={conteudoRef} data-testid="conferencia-conteudo"
      className="sp-page" style={{ color: C.text, maxWidth: 1040 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 22, letterSpacing: '-0.02em' }}>✔ Conferência de Entrega</h2>
        {rows.length > 0 && (
          <span style={{ fontFamily: 'monospace', fontSize: 11, padding: '3px 10px', borderRadius: 6,
            color: divergences ? C.digital : C.cyber, background: divergences ? `${C.digital}1F` : `${C.cyber}1F`,
            border: `1px solid ${(divergences ? C.digital : C.cyber)}55`, letterSpacing: '0.04em' }}>
            {divergences ? `${divergences} DIVERGÊNCIA${divergences !== 1 ? 'S' : ''}` : 'ADERÊNCIA TOTAL'}
          </span>
        )}
        <button onClick={() => generate.mutate()}
          disabled={generate.isPending || !pagina.podeConferir}
          title={pagina.motivoConferir || undefined}
          style={{ marginLeft: 'auto',
            cursor: generate.isPending || !pagina.podeConferir ? 'default' : 'pointer',
            background: C.cyber, color: C.black, border: 'none', borderRadius: 8,
            padding: '9px 16px', fontSize: 13, fontWeight: 700,
            opacity: generate.isPending || !pagina.podeConferir ? 0.6 : 1 }}>
          {generate.isPending ? 'Conferindo…' : rows.length ? '↻ Reconferir' : '✨ Conferir entrega com IA'}
        </button>
        <ExportarConferenciaMenu
          disabled={rows.length === 0 || generate.isPending}
          motivo={rows.length === 0
            ? 'Ainda não há conferência para exportar — gere a conferência primeiro.'
            : generate.isPending ? 'Aguarde a conferência terminar.' : ''}
          ocupado={exportando}
          onExportar={exportar}
        />
      </div>
      {erroExport && (
        <div role="alert" data-export="omitir" style={{ marginTop: 10, color: C.hot, fontSize: 12.5 }}>
          Falha ao exportar: {erroExport}
        </div>
      )}
      <AiProgress busy={busy} label={remediate.isPending ? 'Corrigindo lacunas' : 'Conferindo a entrega'} />
      <p style={{ color: C.dim, maxWidth: '80ch', marginTop: 10, fontSize: 13.5, lineHeight: 1.5 }}>
        Matriz "pedido (requisitos) vs. entregue (arquivos)" gerada pelo QA (via AI Core/Claude),
        mapeando os requisitos reais contra os artefatos reais — critérios de aceite com evidência,
        testes e telas. Aceite humano antes do <strong style={{ color: C.text }}>Gate Final</strong>.
      </p>

      <OrigemDaEntrega entrega={data?.entrega} analise={isAnalise} />

      {/* Quando rodar */}
      <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 8, background: C.surface2,
        border: `1px solid ${C.border}`, color: C.dim, fontSize: 12.5, maxWidth: '80ch' }}>
        <strong style={{ color: C.text }}>Quando rodar:</strong> depois que a squad concluiu a execução
        e <strong>entregou o código</strong> (requisitos extraídos + artefatos gerados). É o passo final
        de verificação antes do Gate Final — compara o que foi pedido com o que foi entregue.
      </div>

      {generate.isError && (
        generate.error?.status === 409 ? (
          <div style={{ marginTop: 12, padding: '12px 16px', borderRadius: 8, color: C.warn,
            background: 'rgba(255,176,32,0.10)', border: '1px solid rgba(255,176,32,0.35)', fontSize: 13 }}>
            Ainda não dá para conferir: este projeto não tem uma <strong>execução concluída</strong> com
            requisitos e código entregue. Rode a execução (até o Gate B / geração de código) e volte aqui.
          </div>
        ) : (
          <div style={{ marginTop: 12, color: C.hot, fontSize: 13 }}>
            Falha: {String(generate.error?.title || generate.error?.message || generate.error)}
          </div>
        )
      )}
      {isLoading && <div style={{ marginTop: 20, color: C.dim }}>Carregando…</div>}
      {!isLoading && rows.length === 0 && (
        <div style={{ marginTop: 24, padding: 28, border: `1px dashed ${C.border}`, borderRadius: 12,
          color: C.dim, textAlign: 'center' }}>
          Sem conferência ainda. Após a execução entregar o código, clique em{' '}
          <strong style={{ color: C.text }}>Conferir entrega com IA</strong>.
        </div>
      )}

      {rows.length > 0 && (
        <>
          <AiTruncationNotice complete={data?.aiComplete} onRetry={() => generate.mutate()} />
          {/* Veredito FORMAL da plataforma (executou o código) vem antes da leitura do QA:
              os dois podiam se contradizer na mesma página sem dizer qual vale (2026-10-01). */}
          {!isAnalise && data?.assurance && (
            <div style={{ marginTop: 18, border: `1px solid ${C.border}`, background: C.surface,
              borderRadius: 12, padding: '14px 18px', display: 'flex', alignItems: 'center',
              gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Veredito da plataforma</span>
              <SeloDeGarantia estado={data.assurance.estado} />
              <span style={{ fontSize: 12.5, color: C.dim }}>
                {textoDoVeredito(data.assurance)}
              </span>
            </div>
          )}
          {/* Veredito */}
          <div style={{ marginTop: 18, border: `1px solid ${scoreColor}44`, background: C.surface,
            borderRadius: 12, padding: 18 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 42, fontWeight: 800, color: scoreColor, lineHeight: 1 }}>{score}%</span>
              <span style={{ fontSize: 13.5, color: C.dim }}>
                Aderência da entrega ao que foi aprovado nos Gates de Negócio e Tecnologia
              </span>
            </div>
            <div style={{ height: 6, background: C.surface2, borderRadius: 4, marginTop: 12, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${score}%`, background: scoreColor,
                boxShadow: `0 0 8px ${scoreColor}88`, transition: 'width 400ms' }} />
            </div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 10 }}>
              {criteriaTotal > 0
                ? `${criteriaOk} de ${criteriaTotal} critérios de aceite verificados com evidência`
                : `${okReqs} de ${rows.length} requisitos atendidos`} · leitura do qa-expert (via Claude)
              pelos nomes dos arquivos — não executa o código
            </div>
            {isAnalise && (data?.nfrTotal ?? 0) > 0 && (
              <div style={{ fontSize: 12, color: C.dim, marginTop: 8, padding: '8px 10px',
                borderRadius: 8, background: C.surface2, border: `1px solid ${C.border}` }}>
                📐 Cobertura de <strong style={{ color: C.text }}>conteúdo</strong> (o que a análise entrega).
                {' '}<strong style={{ color: C.text }}>{data.nfrDocumented ?? 0}/{data.nfrTotal}</strong> requisitos de
                processo/NFR (SLA, precisão, runtime) são documentados via metodologia/limitações — contados à
                parte, pois não são entregáveis por análise estática.
              </div>
            )}
          </div>

          {/* Cobertura de TELAS do design aprovado — número honesto, separado da aderência
              por requisitos acima. Só p/ fullstack com telas de design registradas. */}
          {!isAnalise && screensTotal > 0 && (
            <div style={{ marginTop: 14, border: `1px solid ${designColor}55`, background: `${designColor}0A`,
              borderRadius: 12, padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 32, fontWeight: 800, color: designColor, lineHeight: 1 }}>{designPct}%</span>
                <span style={{ fontSize: 20, fontWeight: 700, color: C.text }}>{screensOk}/{screensTotal} telas</span>
                <span style={{ fontSize: 13, color: C.dim }}>
                  do design aprovado têm UI navegável entregue
                </span>
              </div>
              <div style={{ height: 6, background: C.surface2, borderRadius: 4, marginTop: 12, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${designPct}%`, background: designColor,
                  boxShadow: `0 0 8px ${designColor}88`, transition: 'width 400ms' }} />
              </div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 10, lineHeight: 1.5, maxWidth: '82ch' }}>
                A aderência de <strong style={{ color: C.text }}>{score}%</strong> acima mede os
                {' '}<strong style={{ color: C.text }}>requisitos</strong> aprovados — que são um <em>resumo</em> do
                design. Esta barra mede as <strong style={{ color: C.text }}>telas</strong> que o cliente aprovou no
                Design Studio. Quando os dois números divergem muito, há telas planejadas que ainda não viraram UI.
              </div>
            </div>
          )}

          {/* REMEDIAR POR PROMPT — cliente completa/conserta a entrega (tipo Claude Code do
              produto): badges por superfície + chips de lacuna + caixa de texto livre.
              OCULTO por decisão do dono (2026-09-22): com a construção em ordem por
              camadas, a entrega sai completa de uma vez e o caminho de conserto passou a
              ser regenerar, não remendar por cima. O bloco fica no código, atrás da flag,
              porque a funcionalidade continua valendo — é a exibição que saiu. */}
          {MOSTRAR_REMEDIACAO && !isAnalise && screensTotal > 0 && (
            <div style={{ marginTop: 14, border: `1px solid ${C.border}`, background: C.surface,
              borderRadius: 12, padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ color: C.digital }}>🛠</span>
                <h3 style={{ margin: 0, fontSize: 14, color: C.text }}>Remediar — completar / consertar a entrega</h3>
              </div>
              <p style={{ fontSize: 12, color: C.dim, margin: '0 0 12px', lineHeight: 1.5, maxWidth: '82ch' }}>
                Peça pro sistema completar o que falta ou consertar algo — em cima da entrega atual,
                sem regenerar do zero. Cada rodada acumula; o PR sai como proposta (você aprova).
              </p>

              {remState.blocked && (
                <div role="status" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                  border: `1px solid ${C.cyber}88`, background: `${C.cyber}14`, borderRadius: 10,
                  padding: '10px 14px', marginBottom: 12 }}>
                  <span style={{ fontSize: 18 }}>⏳</span>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <div style={{ color: C.text, fontSize: 13.5, fontWeight: 700 }}>
                      {remLaunched ? 'Remediação iniciada — em andamento' : 'Execução em andamento neste projeto'}
                    </div>
                    <div style={{ color: C.dim, fontSize: 12, marginTop: 2 }}>
                      Execução {String(remState.activeExecutionId).slice(0, 8)}… · pode levar de minutos a horas.
                      Um novo pedido fica liberado quando ela terminar; depois clique em Reconferir.
                    </div>
                  </div>
                  <button type="button"
                    onClick={() => navigate(`/execution/liveboard?execution=${remState.activeExecutionId}`)}
                    style={{ cursor: 'pointer', background: C.cyber, color: C.black, border: 'none',
                      borderRadius: 8, padding: '7px 14px', fontSize: 12.5, fontWeight: 700 }}>
                    Acompanhar no Live Board →
                  </button>
                </div>
              )}

              {/* Cobertura POR SUPERFÍCIE (verdade honesta — web e mobile separados) */}
              {Object.keys(surfaceCoverage).length > 0 && (
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                  {Object.entries(surfaceCoverage).map(([s, v]) => {
                    const gap = surfaceGap.includes(s);
                    const col = gap ? C.hot : C.cyber;
                    return (
                      <div key={s} style={{ border: `1px solid ${col}55`, background: `${col}0A`,
                        borderRadius: 8, padding: '6px 12px', display: 'flex', alignItems: 'baseline', gap: 6 }}>
                        <span style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{s}</span>
                        <strong style={{ color: col, fontSize: 15 }}>{v.screens}/{screensTotal}</strong>
                        <span style={{ fontSize: 12, color: col }}>({v.pct}%)</span>
                        {gap && <span style={{ fontSize: 10.5, color: C.hot }}>⚠ raso</span>}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Chips de lacuna — clique pré-preenche a superfície + um prompt sugerido */}
              {surfaceGap.length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                  {surfaceGap.map((s) => (
                    <button key={s} type="button"
                      onClick={() => { setRemSurface(s); setRemPrompt(`Complete TODAS as telas do escopo no ${s} (paridade de UI).`); }}
                      style={{ cursor: 'pointer', fontSize: 12, padding: '4px 10px', borderRadius: 6,
                        background: C.surface2, color: C.text, border: `1px solid ${C.hot}66` }}>
                      + completar {s} ({surfaceCoverage[s]?.pct ?? 0}%)
                    </button>
                  ))}
                </div>
              )}

              {/* Superfície-alvo */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: C.muted }}>Superfície:</span>
                {['', 'web', 'mobile'].map((s) => (
                  <button key={s || 'auto'} type="button" onClick={() => setRemSurface(s)}
                    style={{ cursor: 'pointer', fontSize: 12, padding: '4px 12px', borderRadius: 6,
                      background: remSurface === s ? C.digital : C.surface2,
                      color: remSurface === s ? C.black : C.muted, border: `1px solid ${C.border}`,
                      fontWeight: remSurface === s ? 700 : 400 }}>
                    {s || 'auto'}
                  </button>
                ))}
              </div>

              {/* Caixa de prompt livre */}
              <textarea value={remPrompt} onChange={(e) => setRemPrompt(e.target.value)} rows={3}
                disabled={remState.blocked}
                placeholder="Descreva o que falta ou o que consertar — ex.: 'faltam as telas de Configurações no mobile', 'a tela de export LGPD precisa de botão de confirmar'…"
                style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', background: C.surface2,
                  color: C.text, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10,
                  fontSize: 13, fontFamily: 'inherit', lineHeight: 1.5 }} />

              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
                <button type="button" onClick={runDirectedRemediation}
                  disabled={!remState.canSubmit}
                  style={{ cursor: remState.canSubmit ? 'pointer' : 'default',
                    background: C.digital, color: C.black, border: 'none', borderRadius: 8,
                    padding: '9px 16px', fontSize: 13, fontWeight: 700,
                    opacity: remState.canSubmit ? 1 : 0.6 }}>
                  {aguardaRemedNew ? 'Aguardando indexação…'
                    : remediateNew.isPending ? 'Disparando remediação…'
                    : remState.blocked ? '⏳ Remediação em andamento' : '🛠 Remediar'}
                </button>
                <span style={{ fontSize: 11.5, color: C.muted }}>
                  Brownfield — acumula sobre a entrega atual. Ao terminar, clique em Reconferir p/ atualizar a cobertura.
                </span>
              </div>
              {indexRemedNew.fase !== 'ocioso' && (
                <div style={{ marginTop: 8 }}>
                  <AvisoIndexacao estado={indexRemedNew} onTentarDeNovo={runDirectedRemediation} />
                </div>
              )}
              {remediateNew.isError && indexRemedNew.fase === 'ocioso' && (
                <div style={{ marginTop: 10, color: C.hot, fontSize: 12.5 }}>
                  Falha ao remediar: {String(remediateNew.error?.detail || remediateNew.error?.title || remediateNew.error?.message || remediateNew.error)}
                </div>
              )}
            </div>
          )}

          {/* Remediação da ANÁLISE — chegar a 100% antes de concluir (semi-auto) */}
          {MOSTRAR_REMEDIACAO && isAnalise && score < 100 && (
            <div style={{ marginTop: 14, border: `1px solid ${C.digital}55`, background: `${C.digital}0A`,
              borderRadius: 12, padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: C.digital }}>⚑</span>
                <h3 style={{ margin: 0, fontSize: 14, color: C.text }}>
                  Análise incompleta — {divergences} requisito{divergences !== 1 ? 's' : ''} sem cobertura total
                </h3>
              </div>
              <p style={{ fontSize: 12.5, color: C.dim, margin: '8px 0 0', lineHeight: 1.5, maxWidth: '80ch' }}>
                Antes de concluir a entrega, uma <strong style={{ color: C.text }}>análise deve cobrir 100%</strong> do
                solicitado. Corrigir vai reprocessar os analistas para gerar o conteúdo que faltou (diagramas,
                inventários, mapeamentos) e documentar a <strong>metodologia/cobertura</strong> dos requisitos de
                processo/NFR — anexando ao Relatório e reconferindo. Cada rodada é aprovada por você.
              </p>
              {remedInfo && (
                <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>
                  Última rodada endereçou {remedInfo.gapsAddressed} de {remedInfo.gapsTotal} lacuna{remedInfo.gapsTotal !== 1 ? 's' : ''}.
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
                <button onClick={runRemediation} disabled={busy || !executionId}
                  style={{ cursor: busy || !executionId ? 'default' : 'pointer', background: C.digital, color: C.black,
                    border: 'none', borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 700,
                    opacity: busy || !executionId ? 0.6 : 1 }}>
                  {remediate.isPending ? 'Corrigindo lacunas…' : generate.isPending ? 'Reconferindo…' : '✨ Corrigir lacunas e reprocessar'}
                </button>
                {busy && (
                  <span style={{ fontSize: 11.5, color: C.muted }}>
                    A IA está trabalhando — pode levar 1-3 min por rodada.
                  </span>
                )}
              </div>
              {remediate.isError && (
                <div style={{ marginTop: 10, color: C.hot, fontSize: 12.5 }}>
                  Falha ao corrigir: {String(remediate.error?.detail || remediate.error?.title || remediate.error?.message || remediate.error)}
                </div>
              )}
            </div>
          )}
          {isAnalise && score >= 100 && (
            <div style={{ marginTop: 14, border: `1px solid ${C.cyber}55`, background: `${C.cyber}0A`,
              borderRadius: 12, padding: '12px 16px', color: C.cyber, fontSize: 13, fontWeight: 600 }}>
              ✓ Análise cobre 100% do solicitado — pronta para concluir no Gate Final.
            </div>
          )}

          {/* KPIs */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginTop: 14 }}>
            <KpiCard label="Requisitos atendidos" value={`${okReqs}/${rows.length}`} color={C.cyber}
              sub="aprovados no Gate de Negócio" />
            <KpiCard label="Critérios verificados" value={criteriaTotal ? `${criteriaOk}/${criteriaTotal}` : '—'} color={C.cyber}
              sub="com evidência vinculada" />
            {design.length > 0 && (
              <KpiCard label="Telas do design" value={`${okScreens}/${design.length}`} color={C.quantum}
                sub="design vs. implementado" />
            )}
            <KpiCard label="Divergências" value={String(divergences)} color={divergences ? C.digital : C.cyber}
              sub={divergences ? 'requer decisão humana' : 'nenhuma'} />
          </div>

          {/* Matriz de rastreabilidade */}
          <div style={{ marginTop: 16, border: `1px solid ${C.border}`, background: C.surface, borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '14px 16px 4px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: C.cyber }}>▦</span>
                <h3 style={{ margin: 0, fontSize: 14, color: C.text }}>Matriz de rastreabilidade — requisito → evidência</h3>
              </div>
              <p style={{ fontSize: 12, color: C.muted, margin: '6px 0 10px' }}>
                Clique em um requisito para ver a cadeia completa: critérios de aceite, arquivos gerados
                (com inspeção), testes que comprovam e a tela do design que atende.
              </p>
            </div>
            {rows.map((r, i) => <TraceItem key={i} r={r} onViewFile={setViewFile} analise={isAnalise} />)}
          </div>

          {/* Prova de dados — obrigações do esquema real × suíte de integração (Onda 2) */}
          {!isAnalise && <ProvaDeDadosSection prova={data?.provaDeDados} />}

          {/* Contrato front ↔ API — grafo estático das chamadas × endpoints (Onda 3) */}
          {!isAnalise && <ContratoFrontApiSection matriz={data?.featureMatrix} />}
          {/* Contract-Driven, Onda 3: os problemas da entrega com dono, ação e requisito. */}
          {!isAnalise && <ProblemasDaEntregaSection manifest={data?.repairManifest} />}
          {!isAnalise && <EvidenciasSection assurance={data?.assurance} />}
          {/* A5 FR-17: quem fez o quê e o que é determinístico — lidos do relatório de garantia */}
          {!isAnalise && data?.assurance?.executionId && (
            <QuemFezDaExecucao executionId={data.assurance.executionId} />
          )}
          {executionId && (
            <div style={{ margin: '12px 0' }}>
              <button type="button"
                onClick={() => navigate(`/execution/liveboard?execution=${executionId}&aba=saude`)}
                aria-label="Ver a saúde da execução: etapas, alertas e o que fazer"
                style={{ cursor: 'pointer', background: 'transparent', color: C.neutral,
                  border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 14px',
                  fontSize: 13, fontWeight: 600 }}>
                Ver a saúde da execução
              </button>
              <span style={{ marginLeft: 10, color: C.neutralDim, fontSize: 12 }}>
                etapas, alertas e o que fazer — com Continuar de onde parou, Refazer etapa e Validar com IA
              </span>
            </div>
          )}
          {!isAnalise && <RuntimeSection runtime={data?.runtime} />}
          {/* Projeto de agente: o roteiro de validação do contrato, caso a caso. */}
          {!isAnalise && <RoteiroSection roteiro={data?.roteiro} />}
          {/* Só existe quando a entrega tem roteiro: é o que o `RoteiroSection` mostra. */}
          {!isAnalise && data?.roteiro?.executionId && (
            <BancadaDoAgente executionId={data.roteiro.executionId} />
          )}

          {/* Conferência do design */}
          {design.length > 0 && (
            <div style={{ marginTop: 16, border: `1px solid ${C.border}`, background: C.surface, borderRadius: 12, padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{ color: C.quantum }}>🎨</span>
                <h3 style={{ margin: 0, fontSize: 14, color: C.text }}>Conferência do design — planejado vs. implementado</h3>
              </div>
              {design.map((d, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                  padding: '8px 0', borderTop: i ? `1px solid ${C.border}` : 'none' }}>
                  <span style={{ color: d.ok ? C.cyber : C.hot, flexShrink: 0 }}>{d.ok ? '✓' : '✗'}</span>
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: C.text, minWidth: 180 }}>{d.screen}</span>
                  <span style={{ fontSize: 12, color: C.dim, flex: 1, minWidth: 160 }}>{d.planned}</span>
                  <span style={{ color: C.muted }}>→</span>
                  <span style={{ fontSize: 12, fontFamily: 'monospace', color: C.dim, flex: 1, minWidth: 160 }}>{d.delivered}</span>
                </div>
              ))}
            </div>
          )}

          {/* Auditoria de Entrega — revisão PROFUNDA de código (lê o código real vs escopo) */}
          {MOSTRAR_AUDITORIA_DE_ENTREGA && !isAnalise && (
            <div style={{ marginTop: 18, border: `1px solid ${C.quantum}55`, background: `${C.quantum}0A`, borderRadius: 12, padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ color: C.quantum }}>🔍</span>
                <h3 style={{ margin: 0, fontSize: 14, color: C.text }}>Auditoria de Entrega — revisão profunda de código</h3>
                {(audit?.rows ?? []).length > 0 && (
                  <span style={{ fontFamily: 'monospace', fontSize: 11, padding: '3px 10px', borderRadius: 6,
                    color: audit.gapsCount ? C.hot : C.cyber, background: audit.gapsCount ? `${C.hot}1F` : `${C.cyber}1F`,
                    border: `1px solid ${(audit.gapsCount ? C.hot : C.cyber)}55` }}>
                    {audit.gapsCount ? `${audit.gapsCount} GAP${audit.gapsCount !== 1 ? 'S' : ''}` : 'SEM GAPS'}
                  </span>
                )}
              </div>
              <p style={{ fontSize: 12.5, color: C.dim, margin: '8px 0 0', lineHeight: 1.5, maxWidth: '80ch' }}>
                Diferente da Conferência (que checa se cada requisito tem arquivos), esta auditoria{' '}
                <strong style={{ color: C.text }}>lê o código real linha a linha</strong> e classifica cada requisito como
                implementado de verdade, casca, parcial ou ausente — com <strong>evidência (arquivo:linha)</strong>. Aponta
                onde a Conferência super/subestimou, o que existe na API mas <strong>não tem tela</strong>, e gera um{' '}
                <strong>.md de correção</strong>. É só leitura — <strong>não altera</strong> seu repositório.
              </p>
              {/* TRILHA DE VALIDAÇÃO — guia a ordem certa (não pular pro "Enviar ao GitHub"). */}
              {(() => {
                const hasConf = rows.length > 0;
                const hasAudit = (audit?.rows ?? []).length > 0;
                const hasGaps = (audit?.gapsCount ?? 0) > 0;
                const trail = [
                  { n: 1, label: 'Conferência gerada', done: hasConf, current: !hasConf },
                  { n: 2, label: 'Auditoria de Entrega (valida vs escopo)', done: hasAudit, current: hasConf && !hasAudit },
                  { n: 3, label: hasGaps ? `Corrigir estritamente pelo .md (${audit.gapsCount} gap${audit.gapsCount !== 1 ? 's' : ''})` : 'Corrigir pelo .md (se houver gaps)',
                    done: hasAudit && !hasGaps, current: hasGaps && !remedStarted, skip: hasAudit && !hasGaps },
                  { n: 4, label: 'Reauditar (confirma que os gaps caíram)', current: remedStarted },
                  { n: 5, label: 'Enviar ao GitHub — só a versão VALIDADA' },
                ];
                return (
                  <div style={{ margin: '10px 0 2px', padding: '10px 12px', borderRadius: 8,
                    background: C.black, border: `1px solid ${C.border}` }}>
                    <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: '0.04em', marginBottom: 6 }}>
                      TRILHA DE VALIDAÇÃO · ordem recomendada
                    </div>
                    {trail.map((s) => (
                      <div key={s.n} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12,
                        padding: '2px 0', color: s.done ? C.cyber : s.current ? C.text : C.muted }}>
                        <span style={{ width: 16, textAlign: 'center' }}>
                          {s.done ? '✓' : s.current ? '▶' : s.skip ? '–' : '○'}
                        </span>
                        <span style={{ fontWeight: s.current ? 700 : 400 }}>{s.n}. {s.label}</span>
                      </div>
                    ))}
                    <div style={{ fontSize: 11, color: C.digital, marginTop: 7, lineHeight: 1.5 }}>
                      ⚠ Valide e corrija <strong>ANTES</strong> de enviar ao GitHub — não pule pro passo 5.
                      O "Enviar ao GitHub" empurra o que você tem; use só quando a auditoria estiver limpa.
                    </div>
                  </div>
                );
              })()}
              <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <button onClick={() => genAudit.mutate()}
                  disabled={genAudit.isPending || !pagina.podeConferir}
                  title={pagina.motivoConferir || undefined}
                  style={{ cursor: genAudit.isPending || !pagina.podeConferir ? 'default' : 'pointer',
                    background: C.quantum, color: C.neutral,
                    border: 'none', borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 700,
                    opacity: genAudit.isPending || !pagina.podeConferir ? 0.6 : 1 }}>
                  {genAudit.isPending ? 'Auditando o código…' : (audit?.rows ?? []).length ? '↻ Reauditar' : '🔍 Rodar auditoria'}
                </button>
                {(audit?.rows ?? []).length > 0 && (
                  <button onClick={downloadAuditSpec}
                    style={{ cursor: 'pointer', background: 'transparent', color: C.dim, border: `1px solid ${C.border}`,
                      borderRadius: 8, padding: '9px 16px', fontSize: 13 }}>
                    ⬇ Baixar .md de correção
                  </button>
                )}
                {MOSTRAR_REMEDIACAO && (audit?.gapsCount ?? 0) > 0 && (
                  <button onClick={runStrictRemediation} disabled={remediateCode.isPending || aguardaRemedCode || remedStarted}
                    title="Dispara um codegen que corrige ESTRITAMENTE os itens do .md, no repo existente (brownfield). Não regenera o projeto."
                    style={{ cursor: (remediateCode.isPending || aguardaRemedCode || remedStarted) ? 'default' : 'pointer', background: C.hot,
                      color: C.black, border: 'none', borderRadius: 8, padding: '9px 16px', fontSize: 13,
                      fontWeight: 700, opacity: (remediateCode.isPending || aguardaRemedCode || remedStarted) ? 0.6 : 1 }}>
                    {aguardaRemedCode ? 'Aguardando indexação…' : remediateCode.isPending ? 'Iniciando…' : remedStarted ? '✓ Remediação iniciada' : '🛠 Corrigir estritamente pelo .md'}
                  </button>
                )}
                {genAudit.isPending && (
                  <span style={{ fontSize: 11.5, color: C.muted }}>Lendo o código real — pode levar 1-4 min.</span>
                )}
              </div>
              {remedStarted && (
                <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 8, color: C.cyber,
                  background: `${C.cyber}0A`, border: `1px solid ${C.cyber}44`, fontSize: 12.5 }}>
                  ✓ Remediação iniciada — o motor corrige só os itens do .md no repo existente.
                  Acompanhe no <strong>Live Board</strong>; quando o novo PR sair, clique{' '}
                  <strong>Reauditar</strong> aqui para confirmar item a item.
                </div>
              )}
              {indexRemedCode.fase !== 'ocioso' && (
                <div style={{ marginTop: 8 }}>
                  <AvisoIndexacao estado={indexRemedCode} onTentarDeNovo={runStrictRemediation} />
                </div>
              )}
              {remediateCode.isError && indexRemedCode.fase === 'ocioso' && (
                <div style={{ marginTop: 10, color: C.hot, fontSize: 12.5 }}>
                  Falha ao iniciar a remediação: {String(remediateCode.error?.detail || remediateCode.error?.message || remediateCode.error)}
                </div>
              )}
              <AiProgress busy={genAudit.isPending} label="Auditando o código entregue" />
              {genAudit.isError && (
                <div style={{ marginTop: 10, color: C.hot, fontSize: 12.5 }}>
                  Falha: {String(genAudit.error?.detail || genAudit.error?.title || genAudit.error?.message || genAudit.error)}
                </div>
              )}
              {(audit?.rows ?? []).length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 10 }}>
                    <KpiCard label="Backend real" value={`${audit.honestBackendPct}%`} color={C.cyber} sub="implementação verificada" />
                    <KpiCard label="Produto navegável" value={`${audit.honestProductPct}%`} color={C.quantum} sub="fluxos com tela no app" />
                    <KpiCard label="Gaps" value={String(audit.gapsCount)} color={audit.gapsCount ? C.hot : C.cyber}
                      sub={`${audit.filesRead || 0} arquivos lidos · fonte ${audit.source || '—'}`} />
                  </div>
                  {(audit.filesNotRead ?? 0) > 0 && (
                    <div role="status" style={{ marginBottom: 10, padding: '8px 12px', borderRadius: 8,
                      color: C.digital, background: `${C.digital}0A`, border: `1px solid ${C.digital}44`,
                      fontSize: 12 }}>
                      ⚠ {audit.filesNotRead} arquivo(s) não couberam no teto de leitura — o que estiver
                      neles não foi conferido: {(audit.filesNotReadSample ?? []).slice(0, 6).join(', ')}
                      {(audit.filesNotReadSample ?? []).length > 6 ? ' …' : ''}
                    </div>
                  )}
                  {audit.summary && (
                    <p style={{ fontSize: 12.5, color: C.dim, lineHeight: 1.5, margin: '0 0 10px' }}>{audit.summary}</p>
                  )}
                  {audit.aiComplete === false && <AiTruncationNotice />}
                  <div style={{ border: `1px solid ${C.border}`, background: C.surface, borderRadius: 10, overflow: 'hidden' }}>
                    {audit.rows.map((r, i) => {
                      const s = AUD_ST[r.realStatus] || AUD_ST.ausente;
                      const v = AUD_VERDICT[r.verdict] || AUD_VERDICT.ok;
                      return (
                        <div key={i} style={{ padding: '10px 14px', borderTop: i ? `1px solid ${C.border}` : 'none' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            <span style={{ fontFamily: 'monospace', fontSize: 10.5, padding: '2px 8px', borderRadius: 5,
                              color: s.color, background: `${s.color}1F`, border: `1px solid ${s.color}55` }}>{s.label}</span>
                            <span style={{ fontSize: 13, fontWeight: 600, color: C.text, flex: 1, minWidth: 180 }}>{r.requirement}</span>
                            {v.label && <span style={{ fontSize: 11, color: v.color, fontWeight: 600 }}>{v.label}</span>}
                            {r.gapKind && <span style={{ fontSize: 10.5, color: C.muted, fontFamily: 'monospace' }}>{r.gapKind}</span>}
                          </div>
                          {r.detail && (
                            <div style={{ fontSize: 12, color: C.dim, marginTop: 4, lineHeight: 1.45 }}>{r.detail}</div>
                          )}
                          {(r.evidence ?? []).length > 0 && (
                            <div style={{ fontSize: 11, color: C.muted, marginTop: 4, fontFamily: 'monospace' }}>
                              {r.evidence.slice(0, 4).map((e, j) => <div key={j}>· {e}</div>)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {(auditHistory ?? []).length > 1 && (
                <div style={{ marginTop: 14, borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
                  <div style={{ fontSize: 12, color: C.muted, marginBottom: 8, fontWeight: 600 }}>
                    📈 Histórico de auditorias (tendência)
                  </div>
                  {auditHistory.slice(0, 6).map((h, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 12,
                      padding: '4px 0', color: C.dim, flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: 'monospace', color: C.muted, minWidth: 130 }}>
                        {String(h.createdAt || '').slice(0, 16).replace('T', ' ')}
                      </span>
                      <span>backend <strong style={{ color: C.cyber }}>{h.backendPct}%</strong></span>
                      <span>produto <strong style={{ color: C.quantum }}>{h.productPct}%</strong></span>
                      <span>gaps <strong style={{ color: h.gapsCount ? C.hot : C.cyber }}>{h.gapsCount}</strong></span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Justificativa + aceite */}
          <div style={{ marginTop: 20, border: `1px solid ${C.border}`, background: C.surface2, borderRadius: 12, padding: 16 }}>
            {isAnalise && score < 100 && (
              <div style={{ marginBottom: 12, padding: '10px 12px', borderRadius: 8, color: C.digital,
                background: `${C.digital}0A`, border: `1px solid ${C.digital}44`, fontSize: 12.5 }}>
                ⚠ A análise ainda não cobre 100% do solicitado. Recomendado{' '}
                <strong style={{ color: C.text }}>corrigir as lacunas e reprocessar</strong> acima antes de concluir
                no Gate Final. Se aceitar assim mesmo, registre a justificativa abaixo.
              </div>
            )}
            <label data-export={comment.trim() ? undefined : 'omitir'} style={{ fontSize: 12.5, color: C.dim }}>
              Justificativa / observações do aceite (obrigatória em caso de divergência)
            </label>
            <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3}
              placeholder="Ex.: divergência do requisito de notificações aceita — será tratada na próxima sprint…"
              style={{ width: '100%', marginTop: 6, background: C.black, color: C.text,
                border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, fontSize: 13, resize: 'vertical' }} />

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: C.dim, flex: 1, minWidth: 220 }}>
                🛡 O aceite formal acontece no <strong style={{ color: C.text }}>Gate Final</strong> e gera evento
                imutável na trilha de auditoria (aderência, divergências e justificativas).
              </span>
              <button onClick={goCorrections} style={{ background: 'transparent', color: C.dim,
                border: `1px solid ${C.border}`, borderRadius: 8, padding: '9px 16px', fontSize: 13, cursor: 'pointer' }}>
                Solicitar correções
              </button>
              <button onClick={goAccept} disabled={!pagina.podeAceitar}
                title={pagina.motivoAceitar || undefined}
                style={{ background: C.cyber, color: C.black, border: 'none',
                  borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 700,
                  cursor: pagina.podeAceitar ? 'pointer' : 'default',
                  opacity: pagina.podeAceitar ? 1 : 0.5 }}>
                Aceitar entrega e revisar commit →
              </button>
            </div>
            {!pagina.podeAceitar && pagina.motivoAceitar && (
              <div data-testid="motivo-aceite" style={{ marginTop: 10, fontSize: 12, color: C.dim }}>
                🔒 {pagina.motivoAceitar}
              </div>
            )}
          </div>
        </>
      )}

      <FileViewerModal file={viewFile} onClose={() => setViewFile(null)} enviada={pagina.enviada} />
    </div>
    </ExportacaoContext.Provider>
  );
}
